/**
 * 休眠の棚卸し（issue #96）と利用状況（issue #115）の画面で使う言葉。
 *
 * 一覧の区切り（週に一度・月曜0時）の定義元はサーバ（`server/src/admin/dormancy-service.ts`）。
 * ここは、サーバが返した日時を**読む方の言葉**にするだけ。
 */
import type { DormancyWindow } from '../api/types.js';

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

const DATE_PARTS = new Intl.DateTimeFormat('ja-JP', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

/** 「9月21日（月）」の形（日本時間）。読めない値はそのまま出す。 */
export function monthDayWithWeekday(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const parts: Record<string, string> = {};
  for (const part of DATE_PARTS.formatToParts(date)) parts[part.type] = part.value;
  // 曜日は日本時間の日付から数える（端末の時計の地域に左右されない）
  const jst = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  return `${parts.month}月${parts.day}日（${WEEKDAYS[jst.getUTCDay()]}）`;
}

/** 「2026年9月21日」の形（日本時間）。値が無いときは `empty`。 */
export function fullDate(value: string | null, empty = '―'): string {
  if (value === null || value === '') return empty;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const parts: Record<string, string> = {};
  for (const part of DATE_PARTS.formatToParts(date)) parts[part.type] = part.value;
  return `${parts.year}年${parts.month}月${parts.day}日`;
}

/**
 * 一覧の区切りの説明（1文）。
 * 例: 「9月21日（月）の時点で、90日以上ログインしていない方です。次に新しくなるのは9月28日（月）です。」
 */
export function windowSentence(window: DormancyWindow, subject: string): string {
  return (
    `${monthDayWithWeekday(window.reference_at)}の時点で、${window.inactive_days}日以上${subject}です。` +
    `一覧は週に一度（月曜）新しくなります。次は${monthDayWithWeekday(window.next_review_at)}です。`
  );
}

/** 「2026-09」→「2026年9月」。 */
export function monthLabel(month: string): string {
  const [year, mon] = month.split('-');
  if (!year || !mon) return month;
  return `${year}年${Number(mon)}月`;
}

/** 「2026-09」→「9月」（推移の表の短い言い方）。 */
export function shortMonthLabel(month: string): string {
  const mon = month.split('-')[1];
  return mon ? `${Number(mon)}月` : month;
}

/** `YYYY-MM` を n か月ずらす。 */
export function shiftMonth(month: string, delta: number): string {
  const [year, mon] = month.split('-').map(Number) as [number, number];
  const shifted = new Date(Date.UTC(year, mon - 1 + delta, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** 人数・件数を「1,234」の形にする。 */
export function count(n: number): string {
  return n.toLocaleString('ja-JP');
}
